import React, { useState, useEffect } from 'react';
import { Lightbulb, Plus, Trash2, Save, RotateCcw } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';

// Beheer van slimme suggestieregels (Instellingen -> Suggesties).
// Elke regel: keywords (alle moeten voorkomen in een bonregel) -> tip-tekst.
export default function SuggestiesManager() {
  const { t } = useT();
  const [regels, setRegels] = useState([]);
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => carsysApi.getSuggesties()
    .then(r => { setRegels(r.regels || []); setCustom(!!r.custom); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setRegels(rs => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const updList = (i, k, v) => upd(i, k, v.split(',').map(s => s.trim()).filter(Boolean));
  const addRegel = () => setRegels(rs => [...rs, { id: `sg${Date.now().toString(36)}`, trigger: '', keywords: [], suggesties: [], tekst: '' }]);
  const delRegel = (i) => setRegels(rs => rs.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await carsysApi.updateSuggesties(regels);
      setRegels(r.regels || []);
      setCustom(true);
      toast.success(t('Suggestieregels opgeslagen', 'Suggestieregels opgeslagen'));
    } catch (e) {
      toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!(await confirmDialog(t('Terug naar de standaardlijst? Je eigen aanpassingen vervallen.', 'Terug naar de standaardlijst? Je eigen aanpassingen vervallen.')))) return;
    try {
      const r = await carsysApi.resetSuggesties();
      setRegels(r.regels || []);
      setCustom(false);
      toast.success(t('Standaardlijst hersteld', 'Standaardlijst hersteld'));
    } catch { toast.error(t('Mislukt')); }
  };

  return (
    <div data-testid="suggesties-manager">
      <h3 className="cs-card-title"><Lightbulb size={16} /> {t('Slimme suggesties', 'Slimme suggesties')}</h3>
      <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginTop: 4 }}>
        {t('Wanneer een bonregel een van de trefwoorden bevat, toont de bon-editor eenmalig een tip. Trefwoorden en suggesties scheid je met komma\'s; álle trefwoorden moeten voorkomen.', 'Wanneer een bonregel een van de trefwoorden bevat, toont de bon-editor eenmalig een tip. Trefwoorden en suggesties scheid je met komma\'s; álle trefwoorden moeten voorkomen.')}
        {' '}{custom ? t('(eigen lijst actief)', '(eigen lijst actief)') : t('(standaardlijst actief)', '(standaardlijst actief)')}
      </p>

      <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--cs-border)', borderRadius: 8 }}>
        <table className="cs-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 130 }}>{t('Onderwerp', 'Onderwerp')}</th>
              <th style={{ width: 160 }}>{t('Trefwoorden', 'Trefwoorden')}</th>
              <th>{t('Tip-tekst', 'Tip-tekst')}</th>
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {regels.map((r, i) => (
              <tr key={r.id || i}>
                <td><input className="cs-input" style={{ padding: 4 }} value={r.trigger || ''} onChange={e => upd(i, 'trigger', e.target.value)} placeholder={t('Koppeling')} /></td>
                <td><input className="cs-input" style={{ padding: 4 }} value={(r.keywords || []).join(', ')} onChange={e => updList(i, 'keywords', e.target.value)} placeholder={t('koppeling')} /></td>
                <td><input className="cs-input" style={{ padding: 4 }} value={r.tekst || ''} onChange={e => upd(i, 'tekst', e.target.value)} placeholder={t('Drukgroep en druklager worden vaak samen met een koppeling vervangen.')} /></td>
                <td><button className="cs-btn ghost danger" onClick={() => delRegel(i)} title={t('Verwijderen')}><Trash2 size={12} /></button></td>
              </tr>
            ))}
            {regels.length === 0 && <tr><td colSpan={4} className="cs-empty">{t('Geen regels.', 'Geen regels.')}</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
        <button data-testid="btn-suggestie-add" className="cs-btn" onClick={addRegel}><Plus size={14} /> {t('Regel toevoegen', 'Regel toevoegen')}</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="btn-suggestie-reset" className="cs-btn" onClick={handleReset}><RotateCcw size={14} /> {t('Standaardlijst', 'Standaardlijst')}</button>
          <button data-testid="btn-suggestie-save" className="cs-btn primary" onClick={handleSave} disabled={saving}><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}
